/**
 * Admin Inventory Report — stock health, low/out, value, category mix.
 *
 * Backed by GET /api/admin/reports/inventory.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import {
    Loader2,
    Boxes,
    AlertTriangle,
    PackageX,
    BadgeIndianRupee,
} from "lucide-react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Cell,
} from "recharts";
import { ExportButtons } from "@/lib/exportData";

const PALETTE = ["#1B1B1F", "#6E7B5B", "#C07A5A", "#A8B3C2", "#D8C3A5", "#3D3934"];

function Stat({ icon: Icon, label, value, sub, tone = "neutral" }) {
    const toneCls =
        tone === "warn"
            ? "text-[#8A6A2A]"
            : tone === "danger"
            ? "text-[#9A2C2C]"
            : "text-[#1B1B1F]";
    return (
        <div className="rounded-2xl border border-[#E7DED2] bg-white p-5">
            <div className="flex items-center gap-2 text-[10px] font-mono-ui tracking-[0.18em] uppercase text-[#857C72]">
                <Icon className="h-3.5 w-3.5" /> {label}
            </div>
            <p className={`font-display text-3xl mt-2 ${toneCls}`}>{value}</p>
            {sub && <p className="text-xs text-[#857C72] mt-1">{sub}</p>}
        </div>
    );
}

export default function AdminReportsInventory() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("low"); // 'low' | 'out'

    useEffect(() => {
        setLoading(true);
        api.get("/admin/reports/inventory")
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, []);

    const catChart = useMemo(() => {
        if (!data) return [];
        return (data.by_category || []).map((c) => ({
            ...c,
            valueInr: (c.value || 0) / 100,
        }));
    }, [data]);

    const list = tab === "low" ? data?.low_stock || [] : data?.out_of_stock || [];

    return (
        <div className="space-y-6" data-testid="admin-reports-inventory">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">Reports</p>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Inventory report</h1>
                    <p className="text-xs text-[#857C72] mt-1">
                        Stock health, low-stock alerts, and inventory value at retail price.
                    </p>
                </div>
                {data && (
                    <ExportButtons
                        rows={data.by_category || []}
                        filename="xplosiv_package_availability_by_event_type"
                        columns={["category", "skus", "on_hand", "available", "value"]}
                    />
                )}
            </header>

            {loading || !data ? (
                <div className="flex items-center justify-center py-16 text-[#857C72]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading inventory…
                </div>
            ) : (
                <>
                    <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <Stat icon={Boxes} label="SKUs" value={data.skus_total} sub={`${data.on_hand} units on hand`} />
                        <Stat icon={Boxes} label="Available" value={data.available} sub={`${data.reserved} reserved`} />
                        <Stat
                            icon={AlertTriangle}
                            label="Low stock"
                            value={data.low_stock_count}
                            tone={data.low_stock_count > 0 ? "warn" : "neutral"}
                            sub="below threshold"
                        />
                        <Stat
                            icon={PackageX}
                            label="Out of stock"
                            value={data.out_of_stock_count}
                            tone={data.out_of_stock_count > 0 ? "danger" : "neutral"}
                        />
                        <Stat
                            icon={BadgeIndianRupee}
                            label="Inventory value"
                            value={formatPrice(data.inventory_value || 0)}
                            sub="at retail price"
                        />
                    </section>

                    <section className="rounded-2xl border border-[#E7DED2] bg-white p-5">
                        <p className="font-display text-xl">Value by category</p>
                        <div className="h-72 mt-4">
                            {catChart.length === 0 ? (
                                <div className="h-full flex items-center justify-center text-xs text-[#857C72]">
                                    No inventory yet.
                                </div>
                            ) : (
                                <ResponsiveContainer>
                                    <BarChart data={catChart} margin={{ left: 12 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E7DED2" />
                                        <XAxis dataKey="category" stroke="#857C72" fontSize={11} />
                                        <YAxis
                                            stroke="#857C72"
                                            fontSize={11}
                                            tickFormatter={(v) => `\u20B9${v.toLocaleString("en-IN")}`}
                                        />
                                        <Tooltip
                                            formatter={(v) => `\u20B9${Number(v).toLocaleString("en-IN")}`}
                                            contentStyle={{ borderRadius: 12 }}
                                        />
                                        <Bar dataKey="valueInr" radius={[8, 8, 0, 0]}>
                                            {catChart.map((_, i) => (
                                                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </section>

                    <section className="rounded-2xl border border-[#E7DED2] bg-white overflow-hidden">
                        <header className="px-5 py-4 border-b border-[#E7DED2] flex items-center gap-2 flex-wrap">
                            <p className="font-display text-xl flex-1">Alerts</p>
                            <div className="flex items-center gap-1.5">
                                <button
                                    onClick={() => setTab("low")}
                                    className={`px-3 py-1.5 rounded-full text-[11px] font-mono-ui uppercase tracking-[0.12em] border ${
                                        tab === "low"
                                            ? "bg-[#1B1B1F] text-white border-[#1B1B1F]"
                                            : "bg-white text-[#3D3934] border-[#E7DED2] hover:bg-[#FFFCF7]"
                                    }`}
                                    data-testid="inv-tab-low"
                                >
                                    Low ({data.low_stock_count})
                                </button>
                                <button
                                    onClick={() => setTab("out")}
                                    className={`px-3 py-1.5 rounded-full text-[11px] font-mono-ui uppercase tracking-[0.12em] border ${
                                        tab === "out"
                                            ? "bg-[#1B1B1F] text-white border-[#1B1B1F]"
                                            : "bg-white text-[#3D3934] border-[#E7DED2] hover:bg-[#FFFCF7]"
                                    }`}
                                    data-testid="inv-tab-out"
                                >
                                    Out ({data.out_of_stock_count})
                                </button>
                            </div>
                            <ExportButtons
                                rows={list}
                                filename={`xplosiv_package_availability_${tab}`}
                                columns={["title", "category", "sku", "size", "color", "stock", "available", "threshold"]}
                            />
                        </header>
                        {list.length === 0 ? (
                            <div className="p-10 text-center text-xs text-[#857C72]">
                                No {tab === "low" ? "low-stock" : "out-of-stock"} items. Everything is healthy.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[10px] font-mono-ui uppercase tracking-[0.12em] text-[#857C72] border-b border-[#E7DED2] bg-[#FFFCF7]">
                                            <th className="py-3 px-4">Product</th>
                                            <th>SKU</th>
                                            <th>Variant</th>
                                            <th>On hand</th>
                                            <th>Reserved</th>
                                            <th>Available</th>
                                            <th>Threshold</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {list.map((i) => (
                                            <tr key={i.sku} className="border-b border-[#F4EFE6] hover:bg-[#FFFCF7]" data-testid={`inv-${tab}-${i.sku}`}>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-3">
                                                        {i.image ? (
                                                            <img src={i.image} alt="" className="h-9 w-9 rounded-lg object-cover bg-[#F4EFE6]" />
                                                        ) : (
                                                            <div className="h-9 w-9 rounded-lg bg-[#F4EFE6]" />
                                                        )}
                                                        <div>
                                                            <p className="text-xs font-medium">{i.title}</p>
                                                            <p className="text-[10px] font-mono-ui text-[#857C72]">{i.category}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="font-mono text-[11px]">{i.sku}</td>
                                                <td className="text-xs">
                                                    {i.color} / {i.size}
                                                </td>
                                                <td className="text-xs">{i.stock}</td>
                                                <td className="text-xs">{i.reserved}</td>
                                                <td className={`text-xs ${tab === "out" ? "text-[#9A2C2C] font-semibold" : "text-[#8A6A2A] font-semibold"}`}>
                                                    {i.available}
                                                </td>
                                                <td className="text-xs text-[#857C72]">{i.threshold}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    <section className="rounded-2xl border border-[#E7DED2] bg-white overflow-hidden">
                        <header className="px-5 py-4 border-b border-[#E7DED2] flex items-center gap-2">
                            <p className="font-display text-xl flex-1">Inventory by category</p>
                        </header>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-[10px] font-mono-ui uppercase tracking-[0.12em] text-[#857C72] border-b border-[#E7DED2] bg-[#FFFCF7]">
                                        <th className="py-3 px-4">Category</th>
                                        <th>SKUs</th>
                                        <th>On hand</th>
                                        <th>Available</th>
                                        <th>Retail value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.by_category || []).map((c) => (
                                        <tr key={c.category} className="border-b border-[#F4EFE6]" data-testid={`inv-cat-${c.category}`}>
                                            <td className="py-3 px-4 text-xs font-display capitalize">{c.category}</td>
                                            <td className="text-xs">{c.skus}</td>
                                            <td className="text-xs">{c.on_hand}</td>
                                            <td className="text-xs">{c.available}</td>
                                            <td className="text-xs">{formatPrice(c.value || 0)}</td>
                                        </tr>
                                    ))}
                                    {(data.by_category || []).length === 0 && (
                                        <tr>
                                            <td colSpan={5} className="py-8 text-center text-xs text-[#857C72]">
                                                No category data.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </>
            )}
        </div>
    );
}
